import { apiClient } from '@/lib/api-client';
import { auth } from '@/lib/firebase';
import { API_BASE_URL } from '@/lib/config';
import {
  UsageResponse,
  TotalUsersResponse,
  UsageLedgerResponse,
  UsageLedgerSummary,
  PricingSheet,
} from '@/types/usage';

export class UsageService {
  async getUserUsage(firebaseUid: string): Promise<UsageResponse> {
    return apiClient.get<UsageResponse>(`/usage/${firebaseUid}`);
  }

  async getTotalUsers(): Promise<TotalUsersResponse> {
    return apiClient.get<TotalUsersResponse>('/usage/total-users');
  }

  async saveUserPreferences(
    firebaseUid: string,
    prefs: { model_tier?: "basic" | "max" }
  ): Promise<void> {
    await apiClient.post(`/usage/${firebaseUid}/preferences`, prefs);
  }

  // ── Usage ledger (per-request logs, usage page) ──

  private ledgerQuery(opts: {
    dateFrom?: string;
    dateTo?: string;
    model?: string;
    limit?: number;
    cursor?: string;
  }): string {
    const params = new URLSearchParams();
    if (opts.dateFrom) params.set('date_from', opts.dateFrom);
    if (opts.dateTo) params.set('date_to', opts.dateTo);
    if (opts.model) params.set('model', opts.model);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  async getLedger(
    firebaseUid: string,
    opts: { dateFrom?: string; dateTo?: string; model?: string; limit?: number; cursor?: string } = {},
  ): Promise<UsageLedgerResponse> {
    return apiClient.get<UsageLedgerResponse>(
      `/usage/${firebaseUid}/ledger${this.ledgerQuery(opts)}`,
    );
  }

  async getLedgerSummary(
    firebaseUid: string,
    opts: { dateFrom?: string; dateTo?: string } = {},
  ): Promise<UsageLedgerSummary> {
    return apiClient.get<UsageLedgerSummary>(
      `/usage/${firebaseUid}/ledger/summary${this.ledgerQuery(opts)}`,
    );
  }

  async getLedgerTimeseries(
    firebaseUid: string,
    opts: { dateFrom?: string; dateTo?: string } = {},
  ): Promise<{ days: { day: string; premium_usd: number; basic_usd: number; requests: number }[] }> {
    return apiClient.get(
      `/usage/${firebaseUid}/ledger/timeseries${this.ledgerQuery(opts)}`,
    );
  }

  async getPricingSheet(): Promise<PricingSheet> {
    return apiClient.get<PricingSheet>('/usage/pricing/sheet');
  }

  /** Download the CSV export for the selected range and save it locally. */
  async downloadLedgerCsv(
    firebaseUid: string,
    opts: { dateFrom?: string; dateTo?: string; model?: string } = {},
  ): Promise<void> {
    const headers: Record<string, string> = {};
    const user = auth.currentUser;
    if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
    const res = await fetch(
      `${API_BASE_URL}/usage/${firebaseUid}/ledger/export.csv${this.ledgerQuery(opts)}`,
      { headers },
    );
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : 'xynth-usage.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export const usageService = new UsageService();
